import { z } from 'zod';

/**
 * @rynora/contracts/notifications — single source of truth for the
 * notification platform.
 *
 * The router (api/src/services/notification-router.ts) uses these
 * schemas as its outermost validation barrier; the admin-settings UI
 * uses them to render edit controls; user prefs are validated against
 * NotificationPrefsSchema before being persisted.
 *
 * Adding a new notification type:
 *   1. Add the literal to NotificationTypeSchema below.
 *   2. Add a row to the bootstrap registry in
 *      packages/api/src/services/notification-config.ts.
 *   3. Add the type to migration 055_notifications_v2.sql seed list
 *      (so freshly provisioned envs already have the row).
 *   4. (Optional) Author an email template under
 *      packages/email-templates/.
 *   5. Wire a producer to call `route(type, userId, payload)`.
 */

// ---- Taxonomy ----

/**
 * Categories carry policy. The 6-step resolution order in the router
 * keys off these to decide whether a user may opt out, which channels
 * are reachable by default, and which respect quiet hours.
 *
 * - transactional: legally implied opt-in (verify, reset, invoice).
 *   Cannot be opted out of by the user; admin can disable per-type
 *   under exceptional circumstances.
 * - account: security events. Default-on, user opt-out allowed unless
 *   admin pins userOptOutAllowed=false.
 * - operational: product workflow events. Per-event prefs.
 * - billing: subscription + quota.
 * - digest: daily / weekly summary. Opt-in.
 * - product: feature announcements + tips. Opt-in.
 * - marketing: re-engagement + campaigns. Strict separate opt-in,
 *   GDPR / CAN-SPAM rules apply.
 */
export const NotificationCategorySchema = z.enum([
  'transactional',
  'account',
  'operational',
  'billing',
  'digest',
  'product',
  'marketing',
]);
export type NotificationCategory = z.infer<typeof NotificationCategorySchema>;

/** Channels the dispatcher can fan out to. */
export const NotificationChannelSchema = z.enum(['inapp', 'email', 'web_push', 'slack', 'teams', 'discord', 'webhook']);
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

/** Severity drives icon + sort + Slack/Teams card colour. */
export const NotificationSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type NotificationSeverity = z.infer<typeof NotificationSeveritySchema>;

/**
 * Stable wire-codes for every notification type the platform fires.
 *
 * Convention: <domain>.<event>. Renames are breaking changes — channel
 * adapters, frontend bell icons, and customer webhook subscribers all
 * read these.
 */
export const NotificationTypeSchema = z.enum([
  // Transactional (legacy literals kept for back-compat)
  'system',
  'account.welcome',
  'account.verify_email',
  'account.password_reset',
  'account.data_export',
  // Account / security
  'account.new_device',
  'account.password_changed',
  'account.mfa_enabled',
  'account.mfa_disabled',
  'account.email_changed',
  'account.api_key_created',
  'account.api_key_revoked',
  'account.api_key_rotated',
  'account.api_key_expiring',
  'account.role_changed',
  // Platform security (continuous DAST etc.)
  'security.dast_finding',
  // Operational (run lifecycle + reviews + leases)
  'run.failed',
  'run.passed',
  'run.scheduled_completed',
  'run.stalled',
  'schedule.failed',
  'flow.lease_stolen',
  'flow.review_requested',
  'flow.review_decided',
  'flow.review_commented',
  'flow.review_merged',
  'flow.review_reviewer_added',
  // pending-test-ownership-oncall v0.1 (Ship 54) — flake/escalation
  // page to flow.owner_user_id after N consecutive scheduled failures.
  'flow.flake.escalation',
  'team.invite_accepted',
  'team.invite_declined',
  'team.invite_expired',
  // Legacy aliases — kept until producers are migrated.
  'run_failed',
  'run_passed',
  'schedule_failed',
  'team_invite',
  'review_requested',
  'review_reviewer_added',
  'review_decided',
  'review_commented',
  'review_merged',
  'weekly_summary',
  // Quota / billing
  'quota.cloud_browser_threshold',
  'quota.concurrency_limit_hit',
  'billing.trial_ending',
  'billing.payment_succeeded',
  'billing.payment_failed',
  'billing.subscription_status_changed',
  'billing.card_expiring',
  'billing.plan_changed',
  // Digest
  'digest.weekly_summary',
  'digest.daily_summary',
  // Onboarding
  'onboarding.day1',
  'onboarding.day3',
  'onboarding.day7',
  'onboarding.reengage14',
  'onboarding.reengage30',
  // Product / marketing
  'product.announcement',
  'marketing.campaign',
  // Quality OS / Scenario / Automation (extend existing router — not a second system)
  'scenario.assigned',
  'scenario.unassigned',
  'scenario.bulk_assigned',
  'scenario.review_requested',
  'scenario.changes_requested',
  'scenario.approved',
  'scenario.rejected',
  'scenario.ready',
  'scenario.reopened',
  'scenario.archived',
  'requirement.assigned',
  'requirement.created',
  'requirement.updated',
  'requirement.coverage_changed',
  'requirement.uncovered',
  'automation.created',
  'automation.updated',
  'automation.stale',
  'automation.mapping_changed',
  'automation.healing_detected',
  'automation.healing_unstable',
  'execution.started',
  'execution.completed',
  'execution.failed',
  'execution.cancelled',
  'execution.blocked',
  'quality_gate.passed',
  'quality_gate.warning',
  'quality_gate.failed',
  'scenario.import_completed',
  'scenario.import_failed',
  'scenario.export_completed',
  'scenario.export_failed',
  'bulk.operation_completed',
  'bulk.operation_failed',
  // Future Shift-Left / Shift-Right (types only — no producers yet)
  'code.impact_detected',
  'test.review_required',
  'automation.review_required',
  'test.maintenance_required',
  'release.risk_detected',
  // #18 Release Confidence (existing router — no second notification system)
  'release.confidence_changed',
  'release.no_go',
  'release.conditional',
  // #17 Shift-Right production quality (existing router)
  'production.quality_signal',
  'production.issue_detected',
  'production.review_required',
  'production.retest_required',
  'production.resolved',
]);
export type NotificationType = z.infer<typeof NotificationTypeSchema>;

// ---- Per-user preferences (matrix shape) ----

/**
 * Per-channel toggles for one category. When a category lookup is
 * missing, the router falls back to the type-config's
 * `defaultChannels` intersected with the admin's category defaults.
 */
export const ChannelPrefsSchema = z.object({
  inapp: z.boolean().default(true),
  email: z.boolean().default(true),
  web_push: z.boolean().default(false),
  slack: z.boolean().default(false),
  teams: z.boolean().default(false),
  discord: z.boolean().default(false),
  webhook: z.boolean().default(false),
});
export type ChannelPrefs = z.infer<typeof ChannelPrefsSchema>;

/**
 * The matrix: category -> channel -> bool. Optional category keys
 * mean "use admin defaults". Persisted in
 * `notification_preferences.prefs jsonb`.
 */
export const NotificationPrefsMatrixSchema = z.object({
  transactional: ChannelPrefsSchema.partial().optional(),
  account: ChannelPrefsSchema.partial().optional(),
  operational: ChannelPrefsSchema.partial().optional(),
  billing: ChannelPrefsSchema.partial().optional(),
  digest: ChannelPrefsSchema.partial().optional(),
  product: ChannelPrefsSchema.partial().optional(),
  marketing: ChannelPrefsSchema.partial().optional(),
  /**
   * Per-type override slot — fired for a specific type even when the
   * category has been opted out of. Use sparingly; the category is the
   * primary axis users tune.
   */
  perType: z.record(ChannelPrefsSchema.partial()).optional(),
});
export type NotificationPrefsMatrix = z.infer<typeof NotificationPrefsMatrixSchema>;

/**
 * Top-level per-user preferences row. Carries the matrix above plus a
 * handful of standalone knobs (quiet hours, digest delivery, marketing
 * + product opt-in, per-channel webhook URLs).
 *
 * The legacy boolean columns from migration 016 (`email_on_failure`,
 * `slack_on_failure`, …) are preserved as a fallback for existing
 * producers that haven't migrated to the router; we sync them to the
 * matrix via a backfill trigger.
 */
export const NotificationPreferencesSchema = z.object({
  // Legacy flat fields (kept for back-compat)
  email_on_failure: z.boolean(),
  weekly_summary: z.boolean(),
  team_activity: z.boolean(),
  scheduled_run_results: z.boolean(),
  slack_webhook_url: z.string().nullable(),
  webhook_url: z.string().nullable(),
  webhook_secret_set: z.boolean(),
  slack_on_failure: z.boolean(),
  webhook_on_failure: z.boolean(),
  slack_on_passed: z.boolean(),
  webhook_on_passed: z.boolean(),

  // V2 fields (new)
  prefs: NotificationPrefsMatrixSchema.default({}),
  quietHoursStart: z.number().int().min(0).max(23).nullable().default(null),
  quietHoursEnd: z.number().int().min(0).max(23).nullable().default(null),
  digestDeliveryHour: z.number().int().min(0).max(23).nullable().default(null),
  marketingOptIn: z.boolean().default(false),
  productOptIn: z.boolean().default(false),
  timezone: z.string().nullable().default(null),
  teams_webhook_url: z.string().nullable().default(null),
  discord_webhook_url: z.string().nullable().default(null),
  teams_on_failure: z.boolean().default(true),
  discord_on_failure: z.boolean().default(true),
  teams_on_passed: z.boolean().default(false),
  discord_on_passed: z.boolean().default(false),
});
export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

export const NotificationPreferencesUpdateSchema = NotificationPreferencesSchema.partial().extend({
  webhook_secret: z.string().nullable().optional(),
});
export type NotificationPreferencesUpdate = z.infer<typeof NotificationPreferencesUpdateSchema>;

// ---- Notification row (in-app + WS payload) ----

export const NotificationSchema = z.object({
  id: z.string(),
  type: z.string(), // accept any string at the wire level; producers should still pick from NotificationType.
  title: z.string(),
  message: z.string().optional(),
  category: NotificationCategorySchema.optional(),
  severity: NotificationSeveritySchema.optional(),
  action_url: z.string().nullable().optional(),
  dedup_key: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
  read: z.boolean(),
  created_at: z.string(),
  expires_at: z.string().nullable().optional(),
});
export type Notification = z.infer<typeof NotificationSchema>;

export const ListNotificationsResponseSchema = z.object({
  notifications: z.array(NotificationSchema),
});
export type ListNotificationsResponse = z.infer<typeof ListNotificationsResponseSchema>;

// ---- Type registry (admin-editable) ----

/**
 * One row in `notification_type_config`. Admin-editable through the
 * admin-settings Notifications -> Types tab.
 */
export const NotificationTypeConfigSchema = z.object({
  type: z.string(),
  enabled: z.boolean().default(true),
  category: NotificationCategorySchema,
  severity: NotificationSeveritySchema.default('info'),
  defaultChannels: z.array(NotificationChannelSchema).default(['inapp']),
  templateId: z.string().nullable().default(null),
  dedupTtlSeconds: z.number().int().nonnegative().default(0),
  userOptOutAllowed: z.boolean().default(true),
  description: z.string().default(''),
  updatedBy: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type NotificationTypeConfig = z.infer<typeof NotificationTypeConfigSchema>;

export const NotificationTypeConfigUpdateSchema = NotificationTypeConfigSchema.partial().omit({
  type: true,
  updatedAt: true,
  updatedBy: true,
});
export type NotificationTypeConfigUpdate = z.infer<typeof NotificationTypeConfigUpdateSchema>;

// ---- Email template override (admin-editable) ----

export const EmailTemplateOverrideSchema = z.object({
  templateId: z.string(),
  subject: z.string().nullable().default(null),
  preheader: z.string().nullable().default(null),
  senderName: z.string().nullable().default(null),
  footerHtml: z.string().nullable().default(null),
});
export type EmailTemplateOverride = z.infer<typeof EmailTemplateOverrideSchema>;

// ---- Delivery row ----

export const DeliveryStatusSchema = z.enum(['pending', 'sent', 'failed', 'skipped', 'suppressed']);
export type DeliveryStatus = z.infer<typeof DeliveryStatusSchema>;

export const NotificationDeliverySchema = z.object({
  id: z.string(),
  notificationId: z.string(),
  userId: z.string(),
  channel: NotificationChannelSchema,
  status: DeliveryStatusSchema,
  attemptedAt: z.string(),
  attemptCount: z.number().int().nonnegative(),
  error: z.string().nullable().optional(),
  providerMessageId: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type NotificationDelivery = z.infer<typeof NotificationDeliverySchema>;
